import React, { useContext, useMemo, useState, useRef } from "react";
import { IconButton, Callout, DirectionalHint } from "@fluentui/react";
import AddReportIcon from "../../../../components/SVGComponents/AddReportIcon";
import ChevronRightIcon from "../../../../components/SVGComponents/ChevronRightIcon";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import { getReportTemplateOptions } from "../../helper/templateUtils";
import ConfirmModal from "../AddTab/../ReportModals/ConfirmationModal";
import { isMobile } from "react-device-detect";
import AgenticReportingContext from "../../../../context/agentic-reporting/AgenticReportingContext";
import userContext from "../../../../context/users/UserContext";
import SubMenuWithSearch from "./SubMenuWithSearch";
import "./LLMTopBar.css";

const LLMAddMenu = ({ isChild = false, isDisabled = false }) => {
  const {
    allReportTemplates,
    patientReports,
    childPatientReports,
    handleDropdownWithContext,
    hasUnsavedChanges,
    modality,
    setReportTitle,
    isRestrictionsEnabled,
    userRole,
  } = useContext(studyReportContext);

  const { getAvailableAgents, initializeAgenticTab } = useContext(
    AgenticReportingContext,
  );

  const { enableLlmReport } = useContext(userContext);
  const reports = isChild ? childPatientReports : patientReports;

  const [calloutTarget, setCalloutTarget] = useState(null);
  const [mobileMenuPos, setMobileMenuPos] = useState(null);
  const [confirm, setConfirm] = useState({ show: false, template: null });
  const [expandedGroup, setExpandedGroup] = useState(null);
  const mobileButtonRef = useRef(null);

  const availableAgents = useMemo(() => {
    return getAvailableAgents();
  }, [getAvailableAgents, patientReports]);

  const menuGroups = useMemo(() => {
    const options = getReportTemplateOptions({
      isDSRExists: !!allReportTemplates?.dsrReportTemplates?.length,
      isClientReportExist: !!allReportTemplates?.clientReportTemplates?.length,
      isRestrictionsEnabled: isRestrictionsEnabled,
      modality: modality,
      userRole: userRole,
    });

    const groups = [];

    if (enableLlmReport && availableAgents && availableAgents.length > 0) {
      groups.push({
        label: "Agentic Template",
        type: "agentic",
        items: availableAgents,
      });
    }

    options.forEach((opt) => {
      let items = [];
      if (opt.key === "deeptekReportTemplage")
        items = allReportTemplates?.reportTemplates || [];
      else if (opt.key === "clientReportTemplate")
        items = allReportTemplates?.clientReportTemplates || [];
      else if (opt.key === "radiologistReportTemplate")
        items = allReportTemplates?.radiologistReportTemplates || [];
      else if (opt.key === "rsnaReportTemplage")
        items = allReportTemplates?.rsnaReportTemplates || [];

      if (items.length > 0) {
        groups.push({ label: opt.text, type: "standard", items: items });
      }
    });

    return groups;
  }, [
    allReportTemplates,
    modality,
    isRestrictionsEnabled,
    userRole,
    availableAgents,
    enableLlmReport,
  ]);

  const handleMouseEnter = (e) => {
    const item = e.currentTarget;
    const rect = item.getBoundingClientRect();
    const submenuWidth = 240;
    const buffer = 10;

    const spaceNeeded = rect.right + submenuWidth + buffer;
    const isOverflowing = spaceNeeded > window.innerWidth;

    if (isOverflowing) {
      item.classList.add("opens-left");
      item.classList.remove("opens-right");
    } else {
      item.classList.remove("opens-left");
      item.classList.add("opens-right");
    }
  };

  const handleAgentSelection = (agent) => {
    const baseAgenticTemplate = allReportTemplates?.dsrReportTemplates?.find(
      (tpl) => tpl.title === "Agentic Report",
    );
    if (!baseAgenticTemplate) return;

    const matchingContent = allReportTemplates?.agenticReportTemplates?.find(
      (item) => item.agenticReportTemplateDto?.title === agent.label,
    );

    const baseName = "Agentic Report";
    let counter = 2;
    let newName = `${baseName} ${counter}`;
    const existingTexts = reports.map((r) => r.text);

    while (existingTexts.includes(newName)) {
      counter++;
      newName = `${baseName} ${counter}`;
    }

    const newTabKey = `agentic_${agent.value}_${Date.now()}`;
    const finalReportContent =
      matchingContent?.agenticReportTemplateDto?.template || "";

    const newTemplate = {
      key: newTabKey,
      text: newName,
      templateType: "dsrTemplate",
      isSuperSpecialReport: baseAgenticTemplate.isSuperSpecialReport ?? 0,
      reportDetails: {
        ...JSON.parse(JSON.stringify(baseAgenticTemplate)),
        id: baseAgenticTemplate.id,
        title: newName,
        reportTemplateTitle: agent.label,
        finalReport: finalReportContent,
        report: finalReportContent,
      },
    };

    handleDropdownWithContext(newTemplate, false);
    if (initializeAgenticTab)
      initializeAgenticTab(newTabKey, finalReportContent, agent);
    setCalloutTarget(null);
  };

  const addTemplate = (tpl) => {
    const tabObj = {
      key: tpl.id + Date.now(),
      text: tpl.title,
      templateType: "standard",
      isSuperSpecialReport: tpl.isSuperSpecialReport ?? 0,
      reportDetails: {
        ...tpl,
        reportTemplateId: tpl.id,
        reportTemplateTitle: tpl.title,
      },
    };
    setReportTitle(tpl.title);
    handleDropdownWithContext(tabObj, false);
    if (tpl.title.includes("Agentic")) {
      const content = tpl.report || tpl.finalReport || "";
      if (initializeAgenticTab) initializeAgenticTab(tabObj.key, content);
    }
    setCalloutTarget(null);
  };

  const onTemplateClick = (tpl) => {
    if (hasUnsavedChanges) {
      setConfirm({ show: true, template: tpl });
    } else {
      addTemplate(tpl);
    }
  };

  const handleMobileGroupToggle = (index) => {
    setExpandedGroup(expandedGroup === index ? null : index);
  };

  const handleMenuDismiss = () => {
    setCalloutTarget(null);
    setMobileMenuPos(null);
    setExpandedGroup(null);
  };

  return (
    <>
      {isMobile ? (
        <button
          ref={mobileButtonRef}
          className="llm-add-btn-mobile"
          onClick={(e) => {
            if (calloutTarget) {
              handleMenuDismiss();
            } else {
              const rect = e.currentTarget.getBoundingClientRect();
              setMobileMenuPos(rect);
              setCalloutTarget(e.currentTarget);
            }
          }}
          disabled={isChild || isDisabled}
        >
          <AddReportIcon height={14} width={14} active={!!calloutTarget} />
        </button>
      ) : (
        <IconButton
          className="llm-add-btn"
          onRenderIcon={() => (
            <AddReportIcon height={14} width={14} active={!!calloutTarget} />
          )}
          styles={{ root: { padding: 0, height: 32, width: 34 } }}
          onClick={(e) => setCalloutTarget(e.currentTarget)}
          disabled={isChild || isDisabled}
        />
      )}

      {/* Mobile: fixed overlay — unaffected by Android keyboard viewport resize */}
      {isMobile && calloutTarget && mobileMenuPos && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9997 }}
            onClick={handleMenuDismiss}
          />
          <div
            style={{
              position: "fixed",
              bottom: window.innerHeight - mobileMenuPos.top + 6,
              left: mobileMenuPos.left,
              zIndex: 9998,
              maxHeight: "60vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agentic-report-menu-container mobile">
              {menuGroups.map((group, index) => (
                <div key={index}>
                  <div
                    className="agentic-report-menu-item mobile-group-header"
                    onClick={() => handleMobileGroupToggle(index)}
                  >
                    <span className="temp-label">{group.label}</span>
                    <span className="mobile-chevron">
                      {expandedGroup === index ? "▲" : "▼"}
                    </span>
                  </div>
                  {expandedGroup === index && (
                    <div className="mobile-submenu-inline">
                      <SubMenuWithSearch
                        items={group.items}
                        type={group.type}
                        onSelect={(item) => {
                          group.type === "agentic"
                            ? handleAgentSelection(item)
                            : onTemplateClick(item);
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
              {menuGroups.length === 0 && (
                <div style={{ padding: "10px", textAlign: "center", color: "#aaa" }}>
                  No Options
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Desktop: Fluent UI Callout */}
      {!isMobile && calloutTarget && (
        <Callout
          target={calloutTarget}
          onDismiss={handleMenuDismiss}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          gapSpace={4}
          styles={{
            calloutMain: {
              background: "transparent",
              border: "none",
              boxShadow: "none",
              overflow: "visible",
            },
          }}
        >
          <div className="agentic-report-menu-container">
            {menuGroups.map((group, index) => (
              <div
                key={index}
                className="agentic-report-menu-item"
                onMouseEnter={handleMouseEnter}
              >
                <span className="temp-label">{group.label}</span>
                <span className="menu-arrow-icon">
                  <ChevronRightIcon />
                </span>
                <SubMenuWithSearch
                  items={group.items}
                  type={group.type}
                  onSelect={(item) =>
                    group.type === "agentic"
                      ? handleAgentSelection(item)
                      : onTemplateClick(item)
                  }
                />
              </div>
            ))}
            {menuGroups.length === 0 && (
              <div style={{ padding: "10px", textAlign: "center", color: "#aaa" }}>
                No Options
              </div>
            )}
          </div>
        </Callout>
      )}

      {confirm.show && (
        <ConfirmModal
          onYes={() => {
            addTemplate(confirm.template);
            setConfirm({ show: false, template: null });
          }}
          onCancel={() => setConfirm({ show: false, template: null })}
          show
        />
      )}
    </>
  );
};

export default LLMAddMenu;
